'use strict'

const xlsx = require('xlsx')

const MAX_BATCH_UPSERT_RECORDS = 100000
const HEADER_ROW = 0
const FIRST_DATA_ROW = 2

function cellText(cell) {
    if (!cell) {
        return ''
    }
    const raw = cell.w != null ? cell.w : cell.v
    if (raw == null) {
        return ''
    }
    return String(raw).trim()
}

function excelBatchLimitMessage(recordCount, endIndex, limit = MAX_BATCH_UPSERT_RECORDS) {
    const where = endIndex ? `（資料範圍到第 ${endIndex} 列，請確認尾端是否有多餘內容）` : ''
    return `批次匯入筆數上限為 ${limit} 筆, 本次 ${recordCount} 筆, 請分批上傳${where}`
}

function assertExcelUploadBatchSize(recordCount, limit = MAX_BATCH_UPSERT_RECORDS, endIndex = 0) {
    if (recordCount > limit) {
        throw excelBatchLimitMessage(recordCount, endIndex, limit)
    }
}

/**
 * One pass over the sheet's cell refs. Scanning three times (max column, row bounds, stray
 * cells) costs ~1s on a 100k x 20 sheet, and every caller needs all three answers.
 */
function scanSheet(sheet) {
    const scan = { maxCol: -1, lastRow: -1, used: [] }
    Object.keys(sheet).forEach((cellRef) => {
        if (cellRef.charAt(0) === '!') {
            return
        }
        const cell = xlsx.utils.decode_cell(cellRef)
        if (cell.c > scan.maxCol) {
            scan.maxCol = cell.c
        }
        if (cell.r <= HEADER_ROW) {
            return
        }
        if (!cellText(sheet[cellRef])) {
            return
        }
        if (cell.r > scan.lastRow) {
            scan.lastRow = cell.r
        }
        scan.used.push({ r: cell.r, c: cell.c, ref: cellRef })
    })
    return scan
}

/** Header labels by column index; a blank label means the column is not part of the template. */
function readHeader(sheet, maxCol) {
    const header = []
    for (let c = 0; c <= maxCol; c++) {
        header.push(cellText(sheet[xlsx.utils.encode_cell({ r: HEADER_ROW, c })]))
    }
    return header
}

function findTicketColumn(header) {
    const index = header.indexOf('ticket')
    if (index < 0) {
        return null
    }
    return { index, letter: xlsx.utils.encode_col(index) }
}

/**
 * Any populated data cell outside the header's own columns: either right of `ticket` (the
 * QA report where a 單號 landed in column U) or under a blank header, which reaches the
 * server as an `__EMPTY` field. Returns the topmost-leftmost one.
 */
function findStrayCell(scan, header, ticketColIndex) {
    let first = null
    for (const cell of scan.used) {
        const isStray = cell.c > ticketColIndex || !header[cell.c]
        if (!isStray) {
            continue
        }
        if (!first || cell.r < first.r || (cell.r === first.r && cell.c < first.c)) {
            first = cell
        }
    }
    return first
}

function validateExcelSheets(sheets, limit = MAX_BATCH_UPSERT_RECORDS) {
    const inputKeys = []
    sheets.forEach((sheet) => {
        const scan = scanSheet(sheet)
        if (scan.lastRow < 0) {
            throw '檔案為空'
        }
        // Excel row numbers; data always starts directly under the header row.
        const startIndex = FIRST_DATA_ROW
        const endIndex = scan.lastRow + 1
        const header = readHeader(sheet, scan.maxCol)
        const ticketCol = findTicketColumn(header)
        if (!ticketCol) {
            throw '找不到單號欄位：請確認表頭（第一列）有一欄名稱為「ticket」，用於填寫 Jira 單號。'
        }
        assertExcelUploadBatchSize(endIndex - startIndex + 1, limit, endIndex)
        const stray = findStrayCell(scan, header, ticketCol.index)
        if (stray) {
            throw `第 ${stray.r + 1} 列出現表頭以外的欄位（${stray.ref}），單號必須填在表頭為 ticket 的欄，請刪除多餘欄後重試`
        }
        for (let i = startIndex; i <= endIndex; i++) {
            const key = cellText(sheet[`A${i}`])
            if (!key) {
                throw `第 ${i} 列 key 欄（A${i}）key值為空`
            }
            const ticket = cellText(sheet[`${ticketCol.letter}${i}`])
            if (!ticket) {
                throw `第 ${i} 列 ticket 欄（${ticketCol.letter}${i}）單號為空`
            }
            inputKeys.push(key)
        }
    })
    return inputKeys
}

module.exports = {
    MAX_BATCH_UPSERT_RECORDS,
    assertExcelUploadBatchSize,
    validateExcelSheets,
}
